<template>
    <div>
        <skeleton-loader v-if="loading" type="lib-rows" :count="perPage" />

        <div v-else-if="programs.length === 0" class="lib-empty">
            <small>{{ $t('programs.no_programs_founded') }}</small>
        </div>

        <div v-else class="lib-list">
            <program-card v-for="program in programs" :key="program.objectID" :program="program" with-menu
                @click:delete="showDeleteProgram(program.objectID)" @click:assign="showAssignProgramDialog(program)"
                @click:copy="confirmCopy(program)" @click:folder="showSaveFolder(program)"
                @click:edit="showEditProgramDialog(program)" @mousedown="startDrag"
                @click:share="showShareProgramDialog(program)" :canShare="canShare" />
        </div>

        <kimsa-pagination class="mt-4" @page="onPageChange" :value="page + 1" :pageCount="pageCount" center
            :total-visible="isMobile ? 4 : (totalVisiblePagination || 7)" />

        <save-in-folder-dialog ref="saveInFolder" :current-id="programId" :default-folder="folderId"
            @cancel="programId = null; folderId = null" type="programs" />

        <assign-program-dialog ref="assign" @hide="programId = null" :program-id="programId" :coach-id="coachId" />

        <save-program-dialog ref="saveProgram" editMode @success="onProgramSaved" />

        <program-card v-if="dragging" :program="draggingItem" with-menu class="floating-drag-item" />

        <delete-dragging-button :dragging="dragMoved" @mouseup="showDeleteProgram(draggingItem.objectID)" />

        <kimsa-confirm ref="showDelete" :title="$t('programs.delete_title')" :content="$t('programs.confirm_deleted')"
            :persistent="false" :loading="loadingDelete" @confirm="deleteProgram" @cancel="programId = null"
            :close-on-confirm="false" :alert-mode="true" />

        <share-program-dialog ref="share" />

        <kimsa-confirm ref="confirmCopy" :title="$t('common.duplicate')" :content="$t('programs.confirm_duplicate')"
            :persistent="false" :loading="loadingCopy" @confirm="onCopyProgram" @cancel="copyItem = null"
            :close-on-confirm="false" />
    </div>
</template>

<script>
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { ProgramIndex } from "@/utils/Algolia";
import { db, Timestamp } from "@/utils/Firebase";
import { debounce } from "@/utils/Common";
import ProgramCard from "@/components/common/routines/ProgramCard";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import { mapActions } from "vuex";
import SaveInFolderDialog from "@/components/coach/SaveInFolderDialog";
import AssignProgramDialog from './AssignProgramDialog.vue';
import KimsaPagination from "@/components/common/KimsaPagination";
import SaveProgramDialog from './SaveProgramDialog.vue';
import DeleteDraggingButton from '../../common/DeleteDraggingButton.vue';
import draggingMixin from "../../../mixins/draggingMixin";
import ShareProgramDialog from './ShareProgramDialog.vue';
import algoliaInlineItemMixin from '../../../mixins/algoliaInlineItemMixin';

export default {
    name: "Programs",
    mixins: [draggingMixin, algoliaInlineItemMixin],
    components: { KimsaPagination, SaveInFolderDialog, SkeletonLoader, ProgramCard, KimsaConfirm, AssignProgramDialog, SaveProgramDialog, DeleteDraggingButton, ShareProgramDialog },
    props: {
        search: {
            type: String,
            default: '',
        },
        folder: {
            type: String,
            default: '',
        },
        totalVisiblePagination: {
            type: [Number, String],
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            loadingDelete: false,
            programId: null,
            programs: [],
            coaches: [],
            folderId: null,
            page: 0,
            pageCount: 0,
            perPage: 6,
            copyItem: null,
            loadingCopy: false,
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser.uid
        },
        canShare() {
            return this.isCompany || this.account?.accountType === 'coach_company'
        },
    },
    async mounted() {
        let vm = this
        vm.loading = true
        vm.isCompany ? await vm.loadCoaches() : await vm.loadPrograms()
    },
    methods: {
        ...mapActions(['fetchCompanyCoaches']),
        async loadCoaches() {
            const vm = this
            const coaches = await vm.fetchCompanyCoaches(vm.companyId)
            vm.coaches = [vm.coachId, ...coaches.map(c => c.objectID)]
        },
        goToProgram(id) {
            const vm = this
            let query = {}
            if (vm.$route.query?.studentId) query.studentId = vm.$route.query.studentId
            vm.pushRoute({ name: `${vm.accountType}.routines.program.detail`, params: { uid: id }, query: query })
        },
        async showDeleteProgram(id) {
            const vm = this
            vm.resetDrag()
            vm.programId = id
            vm.$refs.showDelete.show()
        },
        async deleteProgram() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('programs').doc(vm.programId).delete()
                vm.programs = vm.markDeletedInline(vm.programs, vm.programId)
                vm.toast(vm.$t('programs.deleted'))
                vm.programId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        debouncedLoadPrograms: debounce(
            function () {
                this.loadPrograms()
            }, 1000),
        async loadPrograms() {
            const vm = this
            await vm.withLoading(async () => {
                vm.programs = []
                let coaches = vm.coaches.length === 0 ? vm.coachId : vm.coaches.join(' OR coachId:')
                let filter = `coachId: ${coaches}`
                if (vm.folder) filter += ` AND folder: ${vm.folder}`
                let response = await ProgramIndex.search(vm.search, {
                    filters: filter,
                    hitsPerPage: vm.perPage,
                    page: vm.page,
                })
                vm.pageCount = response.nbPages || 0
                vm.programs = vm.filterDeletedInline(response.hits)
            })
        },
        showAssignProgramDialog(program) {
            const vm = this;
            vm.programId = program.objectID
            vm.$refs.assign.show()
        },
        confirmCopy(program) {
            const vm = this
            vm.resetDrag()
            vm.copyItem = program
            vm.$refs.confirmCopy.show()
        },
        async onCopyProgram() {
            const vm = this
            const program = vm.copyItem
            await vm.withLoading(async () => {
                let currentProgram = await db.collection('programs').doc(program.objectID).get()

                let payload = {
                    ...currentProgram.data(),
                    coachId: vm.coachId,
                    name: `${program.name} ${vm.$t('common.copy_template')}`
                }
                delete payload.created_at
                delete payload.objectID
                payload = JSON.parse(JSON.stringify(payload))
                let ref = await db.collection('programs').add({ ...payload, used: 0, created_at: Timestamp.now() })

                vm.toast(vm.$t('programs.copied'))
                vm.$refs.confirmCopy?.hide()
                vm.goToProgram(ref.id)
            }, 'loadingCopy')
        },
        showSaveFolder(program) {
            const vm = this
            vm.programId = program.objectID
            vm.folderId = program.folder || null
            vm.$refs.saveInFolder.show()
        },
        refresh() {
            const vm = this
            vm.loadPrograms()
        },
        onProgramSaved(program) {
            const vm = this
            if (!vm.addItemInline(vm.programs, program, 'folder', vm.folder)) vm.loadPrograms()
        },
        onPageChange(p) {
            const vm = this
            const newPage = Number(p) - 1
            if (newPage === vm.page) return
            vm.page = newPage
            vm.loadPrograms()
        },
        showEditProgramDialog(program) {
            const vm = this;
            vm.$refs.saveProgram.show(program)
        },
        handleDoubleClickDragging(item) {
            const vm = this;
            vm.confirmCopy(item)
        },
        handleClickDragging(item) {
            const vm = this;
            vm.goToProgram(item.objectID)
        },
        showShareProgramDialog(program) {
            const vm = this;
            vm.$refs.share.show(program)
        }
    },
    watch: {
        coaches() {
            this.loadPrograms()
        },
        search(val) {
            this.page = 0
            this.programs = []
            if (!val) return this.loadPrograms()
            return this.debouncedLoadPrograms()
        },
        loading(val) {
            this.$emit('loading', val)
        },
        folder() {
            this.page = 0
            this.programs = []
            this.refresh()
        }
    }
}
</script>

<style scoped>
.lib-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.lib-empty {
    text-align: center;
    padding: 32px 0;
    color: var(--v-txtMuted-base);
}

.floating-drag-item {
    position: fixed;
    z-index: 9999;
    opacity: var(--drag-opacity);
    width: 250px;
    left: 0;
    top: 0;
    transform: translate(var(--drag-x), var(--drag-y));
    pointer-events: none;
}
</style>